import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Skeleton, Grid } from '@mui/material';
import { red, orange, blue, green, purple, grey } from '@mui/material/colors';
import QueueIcon from '@mui/icons-material/Queue';
import HistoryIcon from '@mui/icons-material/History';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import BackupIcon from '@mui/icons-material/Backup';
import api from '../../../services/api';

function Kpi({ icon, label, value, sub, color }) {
    return (
        <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box sx={{ color: color || grey[700], display: 'flex' }}>{icon}</Box>
                    <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                        {label}
                    </Typography>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5, color: color || 'text.primary', fontFamily: 'Montserrat' }}>
                    {value ?? '--'}
                </Typography>
                {sub && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.3}>
                        {sub}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

function KpiSkeleton() {
    return <Card><CardContent><Skeleton height={70} /></CardContent></Card>;
}

export default function KpiCards() {
    const [queue, setQueue] = useState(null);
    const [oldest, setOldest] = useState(null);
    const [vendors, setVendors] = useState(null);
    const [causes, setCauses] = useState(null);
    const [trend7d, setTrend7d] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get('/hermes/queue/status').then(r => r.data).catch(() => null),
            api.get('/hermes/dashboard/oldest_backup').then(r => r.data).catch(() => null),
            api.get('/hermes/dashboard/coverage_by_vendor').then(r => r.data).catch(() => null),
            api.get('/hermes/dashboard/causes_grouped?period=today').then(r => r.data).catch(() => null),
            api.get('/hermes/dashboard/coverage_trend?period=7d').then(r => r.data).catch(() => null),
        ]).then(([q, o, v, c, t]) => {
            setQueue(q);
            setOldest(o);
            setVendors(v);
            setCauses(c);
            setTrend7d(t);
        }).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Grid container spacing={2}>
                {[0, 1, 2, 3, 4].map(i => (
                    <Grid item xs={12} sm={6} md={4} lg={2.4} key={i}><KpiSkeleton /></Grid>
                ))}
            </Grid>
        );
    }

    // Fila agora (já vem por empresa do endpoint)
    const pending = queue?.queue_total || 0;
    const running = queue?.running_total || 0;

    // Vendor pior (menor %)
    const worstVendor = vendors?.length ? vendors[0] : null;

    // Causa #1
    const topCause = causes?.distribution?.length ? causes.distribution[0] : null;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <Kpi icon={<QueueIcon />} label="Fila agora"
                     value={`${pending} pend.`}
                     sub={`${running} rodando`}
                     color={pending > 0 ? blue[600] : grey[600]} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <Kpi icon={<HistoryIcon />} label="Backup mais velho"
                     value={oldest?.host?.host_name || '—'}
                     sub={oldest?.days != null ? `${oldest.days} dias sem backup` : 'sem registro'}
                     color={oldest?.days >= 3 ? red[600] : orange[600]} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <Kpi icon={<WarningIcon />} label="Vendor pior"
                     value={worstVendor ? `${worstVendor.name} ${worstVendor.pct}%` : '—'}
                     sub={worstVendor ? `${worstVendor.ok}/${worstVendor.total} cobertos` : null}
                     color={worstVendor && worstVendor.pct < 90 ? red[600] : orange[600]} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                <Kpi icon={<ErrorIcon />} label="Causa #1 hoje"
                     value={topCause ? topCause.cause : '—'}
                     sub={topCause ? `${topCause.count} ocorrências (${topCause.pct}%)` : 'sem falhas'}
                     color={topCause ? red[600] : green[600]} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2.4}>
                {(() => {
                    const series = trend7d || [];
                    const avg = series.length
                        ? Math.round(series.reduce((s, d) => s + (d.covered || 0), 0) / series.length)
                        : null;
                    const todayRow = series[series.length - 1];
                    return (
                        <Kpi icon={<BackupIcon />} label="Hosts com config nova"
                             value={avg != null ? `${avg}/dia` : '—'}
                             sub={todayRow ? `${todayRow.covered} hoje · média 7d` : null}
                             color={purple[600]} />
                    );
                })()}
            </Grid>
        </Grid>
    );
}
